import { useCallback, useEffect, useMemo, useState } from 'react';

type CheckedState = boolean | 'indeterminate';

interface UseServerSideSelectionOptions<TFilters> {
  /**
   * Resolves all IDs matching the given filters from the server. Typically a `useMutation`'s
   * `mutateAsync` so the caller retains control over loading/error state and tracing.
   */
  resolveMatchingIds: (filters: TFilters) => Promise<string[]>;
  /**
   * Current filter state. Whenever its identity changes the hook re-resolves the matching IDs,
   * so memoize this in the caller to control re-resolution.
   */
  filters: TFilters;
  /**
   * When false, the hook skips resolving matching IDs (e.g. when there's nothing to select).
   */
  enabled?: boolean;
}

export const useServerSideSelection = <TFilters>({
  resolveMatchingIds,
  filters,
  enabled = true,
}: UseServerSideSelectionOptions<TFilters>) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentFilterIds, setCurrentFilterIds] = useState<string[]>([]);

  useEffect(() => {
    let isActive = true;
    setCurrentFilterIds([]);

    if (!enabled) {
      return () => {
        isActive = false;
      };
    }

    resolveMatchingIds(filters)
      .then((ids) => {
        if (isActive) setCurrentFilterIds(ids);
      })
      .catch(() => {
        if (isActive) setCurrentFilterIds([]);
      });

    return () => {
      isActive = false;
    };
  }, [resolveMatchingIds, filters, enabled]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggleSelection = useCallback((id: string, checked: CheckedState) => {
    if (!checked) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    setSelectedIds((prev) => new Set(prev).add(id));
  }, []);

  const toggleSelectAllMatching = useCallback(async () => {
    const matchingIds = await resolveMatchingIds(filters);
    setCurrentFilterIds(matchingIds);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allMatchingSelected = matchingIds.length > 0 && matchingIds.every((id) => next.has(id));

      if (allMatchingSelected) {
        matchingIds.forEach((id) => next.delete(id));
      } else {
        matchingIds.forEach((id) => next.add(id));
      }

      return next;
    });
  }, [resolveMatchingIds, filters]);

  const selectedCount = selectedIds.size;

  const selectedCurrentFilterCount = useMemo(
    () => currentFilterIds.filter((id) => selectedIds.has(id)).length,
    [currentFilterIds, selectedIds],
  );

  const allCurrentFilterIdsSelected =
    currentFilterIds.length > 0 && selectedCurrentFilterCount === currentFilterIds.length;

  const selectionCheckboxState: CheckedState = allCurrentFilterIdsSelected
    ? true
    : selectedCurrentFilterCount > 0
      ? 'indeterminate'
      : false;

  return {
    selectedIds,
    selectedCount,
    currentFilterIds,
    selectedCurrentFilterCount,
    allCurrentFilterIdsSelected,
    selectionCheckboxState,
    isSelected,
    toggleSelection,
    toggleSelectAllMatching,
    clearSelection,
  };
};
